import {authorized} from "./ApiClient";
import {send} from "./http";
import {NewUserBindingModel, UpdateUserBindingModel, UserNoteViewModel, UserViewModel} from "./models";

const user = (username: string) => `/user/${encodeURIComponent(username)}`;

export const UsersApi = {
    // Administration (SiteAdmin)
    getAll: () => authorized<UserViewModel[]>('GET', '/user'),

    // The API generates a password and emails it to the user.
    // currentPassword: when the API asked for it, to make an administrator (see components/PasswordConfirmation.tsx).
    create: (model: NewUserBindingModel, currentPassword?: string) =>
        authorized<UserViewModel>('POST', '/user', { ...model, ...(currentPassword ? { currentPassword } : {}) }),

    get: (username: string) => authorized<UserViewModel>('GET', user(username)),

    update: (username: string, model: UpdateUserBindingModel, currentPassword?: string) =>
        authorized<UserViewModel>('PUT', user(username), { ...model, ...(currentPassword ? { currentPassword } : {}) }),

    // Turns off the user's two-factor authentication and emails them.
    resetMfa: (username: string) => authorized<void>('DELETE', `${user(username)}/mfa`),
    // The user's mailbox works again: clears the bounce flag, and lets the API's emails reach the address again.
    clearEmailUndeliverable: (username: string) => authorized<void>('DELETE', `${user(username)}/email-undeliverable`),

    // Removes the user's picture (they can set another). Returns the user.
    removeAvatar: (username: string) => authorized<UserViewModel>('DELETE', `${user(username)}/avatar`),

    remove: (username: string) => authorized<void>('DELETE', user(username)),

    // Administrators' notes on the account, newest first. The user never sees them.
    getNotes: (username: string) => authorized<UserNoteViewModel[]>('GET', `${user(username)}/notes`),
    // At most 4000 characters (400 too_long).
    addNote: (username: string, text: string) => authorized<UserNoteViewModel>('POST', `${user(username)}/notes`, { text }),
    deleteNote: (username: string, noteId: string) =>
        authorized<void>('DELETE', `${user(username)}/notes/${encodeURIComponent(noteId)}`),

    // Generates a new password, emails it, and re-enables the account.
    resetPassword: (username: string) => authorized<void>('PUT', `${user(username)}/reset-password`),

    // Self-service, signed out. Always succeeds, so it doesn't reveal which addresses have accounts.
    // turnstileToken is from the Turnstile widget (400 turnstile_failed: do the check again); 429 after too many requests
    // from this address, or for the one email address.
    forgotPassword: (emailAddress: string, turnstileToken: string) =>
        send<void>('POST', '/user/forgot-password', { emailAddress, turnstileToken }),

    // From the emailed link (valid 10 minutes).
    resetForgottenPassword: (token: string, newPassword: string) => send<void>('POST', '/user/reset-password', { token, newPassword })
};
